import { isRussian } from "../locales/display.ts";
import { msg } from "../locales/translate.ts";
/** Server timestamps are UTC. Calendar-only values are days, not instants. */
export type ServerDateValue = string | number | Date | null | undefined;

export const DEFAULT_DISPLAY_TIME_ZONE = "UTC";
const calendarDay = /^\d{4}-\d{2}-\d{2}$/;

export function parseServerDate(value: ServerDateValue): Date | null {
	if (value == null || value === "") return null;
	if (value instanceof Date || Object.prototype.toString.call(value) === "[object Date]") {
		const time = (value as Date).getTime();
		return Number.isFinite(time) ? new Date(time) : null;
	}
	if (typeof value === "string") {
		value = value.trim();
		if (!value) return null;
		if (/^-?\d+(?:\.\d+)?$/.test(value)) return parseServerDate(Number(value));
		const match = /^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2}):(\d{2})(?::(\d{2})(?:\.(\d+))?)?(Z|[+-]\d{2}:?\d{2})?)?$/i.exec(value);
		if (!match) return null;
		const [, y, m, d, h, minute, second] = match;
		const day = new Date(`${y}-${m}-${d}T00:00:00Z`);
		if (!Number.isFinite(day.getTime()) || day.toISOString().slice(0, 10) !== `${y}-${m}-${d}`
			|| Number(h ?? 0) > 23 || Number(minute ?? 0) > 59 || Number(second ?? 0) > 59) return null;
		value = value.replace(" ", "T");
		if (calendarDay.test(value)) value += "T00:00:00Z";
		else if (!/(?:Z|[+-]\d{2}:?\d{2})$/i.test(value)) value += "Z";
	} else {
		if (!Number.isFinite(value)) return null;
		value = Math.abs(value) < 10_000_000_000 ? value * 1000 : value;
	}
	const date = new Date(value);
	return Number.isFinite(date.getTime()) ? date : null;
}

type DateFields = Omit<Intl.DateTimeFormatOptions, "timeZone" | "timeZoneName">;
export interface DateDisplayOptions {
	locale?: string;
	format?: DateFields;
	/** Omit only when another part of the same value labels its zone. */
	withZone?: boolean;
	fallback?: string;
}
export interface DateTimeDisplayOptions extends Omit<DateDisplayOptions, "format"> {
	date?: DateFields;
	time?: DateFields;
	separator?: string;
}

/** A separate formatter can be used for a preview of a future user preference. */
export function createDateTimeFormatter(timeZone = DEFAULT_DISPLAY_TIME_ZONE) {
	// Validate and canonicalize the IANA zone before accepting a configuration.
	const zone = new Intl.DateTimeFormat("en", { timeZone }).resolvedOptions().timeZone;
	const formatters = new Map<string, Intl.DateTimeFormat>();
	const formatter = (locale: string | undefined, options: Intl.DateTimeFormatOptions) => {
		const key = JSON.stringify([locale, options]);
		let result = formatters.get(key);
		if (!result) {
			result = new Intl.DateTimeFormat(locale, options);
			if (formatters.size >= 64) formatters.clear();
			formatters.set(key, result);
		}
		return result;
	};
	const zoneFor = (value: ServerDateValue) => typeof value === "string" && calendarDay.test(value.trim()) ? "UTC" : zone;
	const label = (text: string, timeZone: string, withZone = true) => withZone ? `${text} ${timeZone}` : text;
	const date = (value: ServerDateValue, options: DateDisplayOptions = {}) => {
		const parsed = parseServerDate(value);
		if (!parsed) return options.fallback ?? "—";
		const timeZone = zoneFor(value);
		return label(formatter(isRussian() ? "ru-RU" : options.locale, { ...(options.format ?? { year: "numeric", month: "numeric", day: "numeric" }), timeZone }).format(parsed), timeZone, options.withZone);
	};
	const time = (value: ServerDateValue, options: DateDisplayOptions = {}) => {
		const parsed = parseServerDate(value);
		if (!parsed) return options.fallback ?? "—";
		const timeZone = zoneFor(value);
		return label(formatter(isRussian() ? "ru-RU" : options.locale, { ...(options.format ?? { hour: "numeric", minute: "numeric", second: "numeric" }), timeZone }).format(parsed), timeZone, options.withZone);
	};
	const dateTime = (value: ServerDateValue, options: DateTimeDisplayOptions = {}) => {
		if (!parseServerDate(value)) return options.fallback ?? "—";
		return label(date(value, { locale: options.locale, format: options.date, withZone: false })
			+ (options.separator ?? ", ") + time(value, { locale: options.locale, format: options.time, withZone: false }), zoneFor(value), options.withZone);
	};
	const dayKey = (value: ServerDateValue) => {
		const parsed = parseServerDate(value);
		if (!parsed) return "";
		const parts = formatter("en-GB", { timeZone: zoneFor(value), year: "numeric", month: "2-digit", day: "2-digit" }).formatToParts(parsed);
		const part = (type: string) => parts.find(item => item.type === type)?.value;
		return `${part("year")}-${part("month")}-${part("day")}`;
	};
	return { timeZone: zone, date, time, dateTime, dayKey };
}

let displayFormatter = createDateTimeFormatter();

/** Apply a future profile preference before mounting the app (or reload it after saving).
 * This only affects presentation; never use it to build API filter boundaries or exports.
 */
export function setDisplayTimeZone(timeZone: string) {
	displayFormatter = createDateTimeFormatter(timeZone);
}

export const getDisplayTimeZone = () => displayFormatter.timeZone;
export const formatDisplayDate = (value: ServerDateValue, options?: DateDisplayOptions) => displayFormatter.date(value, options);
export const formatDisplayTime = (value: ServerDateValue, options?: DateDisplayOptions) => displayFormatter.time(value, options);
export const formatDisplayDateTime = (value: ServerDateValue, options?: DateTimeDisplayOptions) => displayFormatter.dateTime(value, options);
export const displayDayKey = (value: ServerDateValue) => displayFormatter.dayKey(value);

let browserFormatter: ReturnType<typeof createDateTimeFormatter> | undefined;

/** Browser-local representation of an instant, using its historical DST offset. */
export function localDateTime(value: ServerDateValue, locale?: string) {
	// A calendar day has no time or offset to convert for the user.
	if (typeof value === "string" && calendarDay.test(value.trim())) return null;
	const date = parseServerDate(value);
	if (!date) return null;
	const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
	if (browserFormatter?.timeZone !== timeZone) browserFormatter = createDateTimeFormatter(timeZone);
	const local = browserFormatter;
	const offsetMinutes = -date.getTimezoneOffset();
	const offset = offsetMinutes === 0 ? "UTC" : `UTC${offsetMinutes > 0 ? "+" : "-"}${String(Math.floor(Math.abs(offsetMinutes) / 60)).padStart(2, "0")}:${String(Math.abs(offsetMinutes) % 60).padStart(2, "0")}`;
	return { date: local.date(date, { locale, withZone: false }), time: local.time(date, { locale, withZone: false }), timeZone, offset,
		differentDay: local.dayKey(date) !== displayFormatter.dayKey(date) };
}

/** Plain text for native tooltips and existing tooltip surfaces. Always includes the full local day. */
export function formatLocalDateTimeTooltip(value: ServerDateValue, locale?: string): string | undefined {
	const local = localDateTime(value, locale);
	return local ? msg("templateLocal_a431ced5", { v0: local.date, v1: local.time, v2: local.timeZone, v3: local.offset === "UTC" ? "UTC+00:00" : local.offset }) : undefined;
}
